import { useEffect, useState, useCallback } from "react";
import {
  api,
  newSession,
  getSessionWithMessages,
  distillSession,
  type SessionWithMessages,
} from "./lib/api";
import { LoginForm } from "./components/LoginForm";
import { Chat } from "./components/Chat";
import { ChatList } from "./components/ChatList";
import { SidePanels } from "./components/SidePanels";

type AuthState = "loading" | "out" | "in";

export function App() {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [session, setSession] = useState<SessionWithMessages | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // knowledge/recs panels
  const [chatsKey, setChatsKey] = useState(0); // chat-list sidebar
  const [distilling, setDistilling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load a chat with its transcript + staleness cue (D8) and make it current.
  const openSession = useCallback(async (id: string) => {
    setSession(await getSessionWithMessages(id));
  }, []);

  const boot = useCallback(async () => {
    const res = await api("/sessions/current");
    if (res.status === 401) {
      setAuth("out");
      return;
    }
    const { id } = (await res.json()) as { id: string };
    await openSession(id);
    setAuth("in");
  }, [openSession]);

  useEffect(() => {
    void boot();
  }, [boot]);

  async function onLogin() {
    await boot();
    setRefreshKey((k) => k + 1);
    setChatsKey((k) => k + 1);
  }

  async function logout() {
    await fetch("/api/auth/sign-out", { method: "POST" });
    setAuth("out");
    setSession(null);
  }

  // UC15 — open any chat (chats are always active — no re-open ceremony, D8).
  async function selectChat(id: string) {
    if (id === session?.id) return;
    await openSession(id);
    setRefreshKey((k) => k + 1);
  }

  // UC15/UC17 — start a fresh chat (optionally seeded from a knowledge item).
  const startNewChat = useCallback(
    async (focusKnowledgeId?: string) => {
      const s = await newSession(focusKnowledgeId);
      await openSession(s.id);
      setRefreshKey((k) => k + 1);
      setChatsKey((k) => k + 1);
    },
    [openSession],
  );

  // D8 — distil this chat + regenerate recommendations; stay on the chat.
  async function updateKnowledge() {
    if (!session) return;
    setDistilling(true);
    setError(null);
    try {
      await distillSession(session.id);
      await openSession(session.id); // refresh staleness → 0, keep the same chat
      setRefreshKey((k) => k + 1);
      setChatsKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aktualisierung fehlgeschlagen");
    } finally {
      setDistilling(false);
    }
  }

  if (auth === "loading") {
    return <div className="grid min-h-full place-items-center text-sm text-neutral-400">…</div>;
  }
  if (auth === "out") {
    return <LoginForm onSuccess={onLogin} />;
  }

  const stale = session?.newSinceDistill ?? 0;
  const neverDistilled = session != null && session.last_distilled_at == null;

  return (
    <div className="grid h-full grid-cols-[220px_1fr_380px] grid-rows-[auto_1fr] max-[980px]:grid-cols-[180px_1fr] max-[720px]:grid-cols-1">
      <header className="col-span-full flex items-center gap-3 bg-neutral-900 px-3 py-1.5 text-sm text-white">
        <b className="text-[0.95rem]">Knowledge Bot</b>
        {(stale > 0 || neverDistilled) && (
          <span
            data-testid="staleness"
            className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[11px] text-amber-200"
          >
            {neverDistilled
              ? "noch nicht destilliert"
              : `${stale} neue seit Destillation`}
          </span>
        )}
        {error && (
          <span
            data-testid="error"
            title={error}
            className="max-w-[40%] truncate rounded bg-red-500/25 px-1.5 py-0.5 text-[11px] text-red-100"
          >
            {error}
          </span>
        )}
        <span className="flex-1" />
        <button
          data-testid="distillBtn"
          onClick={updateKnowledge}
          disabled={distilling}
          className="rounded-md bg-white/15 px-2 py-1 text-xs hover:bg-white/25 disabled:opacity-50"
        >
          {distilling ? "Aktualisiere…" : "Wissen & Empfehlungen aktualisieren"}
        </button>
        <button
          data-testid="logoutBtn"
          onClick={logout}
          className="rounded-md bg-white/15 px-2 py-1 text-xs hover:bg-white/25"
        >
          Logout
        </button>
      </header>

      <div className="min-h-0 max-[720px]:hidden">
        <ChatList
          currentId={session?.id ?? null}
          refreshKey={chatsKey}
          onSelect={selectChat}
          onNew={() => startNewChat()}
          onChanged={() => setChatsKey((k) => k + 1)}
        />
      </div>

      <main className="min-h-0 max-[720px]:col-span-full">
        {session && (
          <Chat key={session.id} sessionId={session.id} initialMessages={session.messages} />
        )}
      </main>

      <div className="min-h-0 max-[980px]:hidden">
        <SidePanels
          refreshKey={refreshKey}
          sessionId={session?.id ?? null}
          onEvolve={(knowledgeId) => startNewChat(knowledgeId)}
        />
      </div>
    </div>
  );
}
